// Coach hours, counted per SESSION rather than per class document.
//
// A class spanning several courts is stored as one document PER COURT — see
// addExtraCourtClasses in Schedule.js — and every one of those documents
// carries the same coaches, date and time. Summing documents therefore paid a
// coach twice for a single two-court practice, three times for a three-court
// one. A coach stands on the sand once; how many courts the class spreads
// across is a facility detail, not more work.
//
// Sessions are identified the way Schedule.js identifies court siblings:
// same date and time slot, plus either the repeatGroupId (recurring) or the
// identifying fields (one-off). Two genuinely different classes at the same
// time stay separate — only the court copies of ONE class collapse.

/** Stable identity for "the same class, however many courts it covers". */
export function sessionKey(c) {
  const slot = `${c.date || ''}|${c.startTime || ''}|${c.endTime || ''}`;
  const identity = c.repeatGroupId
    ? `g:${c.repeatGroupId}`
    : `s:${c.sessionType || ''}|${c.eventName || ''}|${c.skillGroup || ''}`;
  return `${slot}|${identity}`;
}

/**
 * One class per session. Keeps the first document seen for each session, so
 * callers can still read sessionType, rates and the rest off the result.
 */
export function dedupeSessions(classes) {
  const seen = new Map();
  for (const c of classes || []) {
    const k = sessionKey(c);
    if (!seen.has(k)) seen.set(k, c);
  }
  return [...seen.values()];
}

/** Paid hours for one session. A class with no times counts as one hour. */
export function sessionHours(c) {
  if (!c?.startTime || !c?.endTime) return 1;
  const [sh, sm] = String(c.startTime).split(':').map(Number);
  const [eh, em] = String(c.endTime).split(':').map(Number);
  const mins = (eh * 60 + (em || 0)) - (sh * 60 + (sm || 0));
  return mins > 0 ? mins / 60 : 0;
}

/** Total coach hours across a list of classes, court duplicates collapsed. */
export function coachHours(classes) {
  return dedupeSessions(classes).reduce((sum, c) => sum + sessionHours(c), 0);
}

/** How many sessions the list represents, court duplicates collapsed. */
export function sessionCount(classes) {
  return dedupeSessions(classes).length;
}
